/**
 * SchedulingAccordion Component
 *
 * Accordion for scheduling options in the approval modal.
 * Shows auto-calculated time by default (collapsed).
 * Allows manual override with date/time picker.
 */

import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { format, addHours, setHours, setMinutes, isBefore, startOfDay, addDays } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { Accordion } from '../shared/Accordion';
import { Button } from '../shared/Button';
import {
  ClockIcon,
  CalendarIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

import 'react-datepicker/dist/react-datepicker.css';

// Quick time slot presets
const QUICK_TIME_SLOTS = [
  { labelKey: 'scheduling.quickTime.morning', hour: 9, icon: '🌅' },
  { labelKey: 'scheduling.quickTime.noon', hour: 12, icon: '☀️' },
  { labelKey: 'scheduling.quickTime.evening', hour: 18, icon: '🌆' },
  { labelKey: 'scheduling.quickTime.night', hour: 21, icon: '🌙' }
];

// Time picker interval (15 minutes)
const TIME_INTERVAL_MINUTES = 15;

/**
 * SchedulingAccordion Component
 */
export const SchedulingAccordion = ({
  autoScheduledTime,
  manualScheduledTime,
  scheduledTime,
  isManualMode,
  cascadeInfo,
  postIntervalHours,
  isLoading,
  error,
  onManualTimeChange,
  onClearManualTime,
  disabled = false
}) => {
  const { t, i18n } = useTranslation('approval');
  const locale = i18n.language === 'de' ? de : enUS;

  // Local state for date picker
  const [selectedDate, setSelectedDate] = useState(null);

  // Format the scheduled time for display
  const formattedScheduledTime = useMemo(() => {
    if (!scheduledTime) return t('scheduling.calculating');

    return format(scheduledTime, 'EEEE, dd. MMMM yyyy HH:mm', { locale });
  }, [scheduledTime, locale, t]);

  // Short format for collapsed accordion title
  const shortFormattedTime = useMemo(() => {
    if (!scheduledTime) return '...';

    return format(scheduledTime, 'dd.MM. HH:mm', { locale });
  }, [scheduledTime, locale]);

  // Generate accordion title based on mode
  const accordionTitle = useMemo(() => {
    if (isLoading) {
      return t('scheduling.loading');
    }

    if (isManualMode) {
      return t('scheduling.manualTitle', { time: shortFormattedTime });
    }

    return t('scheduling.autoTitle', { time: shortFormattedTime });
  }, [isLoading, isManualMode, shortFormattedTime, t]);

  // Handle date selection
  const handleDateChange = useCallback((date) => {
    setSelectedDate(date);
    if (date) {
      onManualTimeChange(date);
    }
  }, [onManualTimeChange]);

  // Handle quick time slot selection
  const handleQuickTimeSlot = useCallback((hour) => {
    const now = new Date();
    let targetDate = setMinutes(setHours(now, hour), 0);

    // If time has passed today, schedule for tomorrow
    if (isBefore(targetDate, now)) {
      targetDate = addDays(targetDate, 1);
    }

    setSelectedDate(targetDate);
    onManualTimeChange(targetDate);
  }, [onManualTimeChange]);

  // Handle revert to auto
  const handleRevertToAuto = useCallback(() => {
    setSelectedDate(null);
    onClearManualTime();
  }, [onClearManualTime]);

  // Filter time slots (only future times, 15-min intervals)
  const filterTime = useCallback((time) => {
    const now = new Date();
    return time > now;
  }, []);

  // Minimum date (today)
  const minDate = useMemo(() => new Date(), []);

  // Maximum date (2 weeks from now)
  const maxDate = useMemo(() => addDays(new Date(), 14), []);

  return (
    <Accordion
      title={accordionTitle}
      icon="📅"
      defaultExpanded={false}
      variant="neutral"
      disabled={disabled || isLoading}
    >
      <div className="space-y-4 pt-4">
        {/* Error Message */}
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center gap-2 text-gray-500">
            <ArrowPathIcon className="w-5 h-5 animate-spin" />
            <span className="text-sm">{t('scheduling.loadingDetails')}</span>
          </div>
        )}

        {!isLoading && !error && (
          <>
            {/* Current Scheduled Time Display */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <ClockIcon className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">
                  {isManualMode
                    ? t('scheduling.selectedTime')
                    : t('scheduling.autoCalculated')}
                </span>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {formattedScheduledTime}
              </p>
              {!isManualMode && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('scheduling.intervalInfo', { hours: postIntervalHours })}
                </p>
              )}
            </div>

            {/* Manual Mode Toggle / Revert Button */}
            {isManualMode && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRevertToAuto}
                className="w-full"
              >
                <ArrowPathIcon className="w-4 h-4 mr-2" />
                {t('scheduling.revertToAuto')}
              </Button>
            )}

            {/* Quick Time Slots */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('scheduling.quickSlots')}
              </label>
              <div className="grid grid-cols-4 gap-2">
                {QUICK_TIME_SLOTS.map((slot) => (
                  <button
                    key={slot.hour}
                    type="button"
                    onClick={() => handleQuickTimeSlot(slot.hour)}
                    disabled={disabled}
                    className="flex flex-col items-center p-2 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="text-lg">{slot.icon}</span>
                    <span className="text-xs text-gray-600">{slot.hour}:00</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Date and Time Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('scheduling.customDateTime')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                {/* Date Picker */}
                <div>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                    <DatePicker
                      selected={selectedDate || scheduledTime}
                      onChange={handleDateChange}
                      dateFormat="dd.MM.yyyy"
                      locale={locale}
                      minDate={minDate}
                      maxDate={maxDate}
                      disabled={disabled}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholderText={t('scheduling.selectDate')}
                    />
                  </div>
                </div>

                {/* Time Picker */}
                <div>
                  <div className="relative">
                    <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                    <DatePicker
                      selected={selectedDate || scheduledTime}
                      onChange={handleDateChange}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={TIME_INTERVAL_MINUTES}
                      timeCaption={t('scheduling.time')}
                      dateFormat="HH:mm"
                      locale={locale}
                      filterTime={filterTime}
                      disabled={disabled}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholderText={t('scheduling.selectTime')}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Cascade Warning */}
            {cascadeInfo.needsCascade && cascadeInfo.affectedStories.length > 0 && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {t('scheduling.cascadeWarning.title')}
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    {t('scheduling.cascadeWarning.description', {
                      count: cascadeInfo.affectedStories.length
                    })}
                  </p>
                </div>
              </div>
            )}

            {/* Minimum Interval Info */}
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <ClockIcon className="w-3 h-3" />
              {t('scheduling.minimumInterval', { hours: postIntervalHours })}
            </div>
          </>
        )}
      </div>
    </Accordion>
  );
};

SchedulingAccordion.propTypes = {
  autoScheduledTime: PropTypes.instanceOf(Date),
  manualScheduledTime: PropTypes.instanceOf(Date),
  scheduledTime: PropTypes.instanceOf(Date),
  isManualMode: PropTypes.bool.isRequired,
  cascadeInfo: PropTypes.shape({
    needsCascade: PropTypes.bool,
    affectedStories: PropTypes.array,
    newSchedules: PropTypes.array
  }),
  postIntervalHours: PropTypes.number.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onManualTimeChange: PropTypes.func.isRequired,
  onClearManualTime: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

export default SchedulingAccordion;
